import { DealerRentalService } from 'src/services/dealer-rental.service';
import { DealerRentalTableData } from 'src/services/models/dealer-rental.service';
import DetailsView from 'src/views/pages/details-view';
import FooterPrivacy from '../../auth/components/baseTemplate/FooterPrivacy';
import ImageLogo from '../../../assets/images/de-full-logo.png';
import { IUser } from '../../../utils/interfaces/user';
import { NUMBER_ZERO } from 'src/utils/constants';
import Spinner from 'src/components/ui/spinner';
import Table from 'src/components/sl-table';
import TableFooter from 'src/components/table/pagination/tableFooter';
import { useLocalization } from 'src/utils/useLocalization';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { formatDate, getBrandByDealerNumber } from 'src/utils/helpers';
import { useEffect, useRef, useState } from 'react';

import './styles.scss';

const DealerRentalPage = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDetailsLoading, setIsDetailsLoading] = useState<boolean>(true);
    const [isEditApiError, setIsEditApiError] = useState<boolean>(false);
    const [summaryData, setSummaryData] = useState<DealerRentalTableData[]>([]);
    const [brand, setBrand] = useState<string>('vw6');
    const [country, setCountry] = useState<string>('USA');
    const [detailsData, setDetailsData] = useState({});
    const [vin, setVin] = useState('');
    const [acNo, setAcNo] = useState<number>();
    const [displayedContent, setDisplayedContent] = useState<
        DealerRentalTableData[]
    >([]);
    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });

    const [open, setOpen] = useState<boolean>(false);
    const componentRef = useRef(null);
    const {
        dealerRentalInventory,
        labelText,
        detailsView: { TITLE_DEALER_RENTAL },
    } = useLocalization();
    const handlePrint = useReactToPrint({
        content: () => {
            if (componentRef.current) {
                const tableStat = (componentRef.current as Element).cloneNode(
                    true,
                );
                const PrintElem = document.createElement('div');
                const header = `<div style='display: flex; flex-direction:row; align-items:center; justify-content:space-between; width:100%; padding-top:28px; padding-left:28px; padding-right:28px;'> <img src='${ImageLogo}' height='19px' width='170px' /> <p style='margin-bottom:0px;'>${formatDate(
                    '',
                )}</p></div><h5 style='margin:28px;'>${
                    dealerRentalInventory.LABEL_DR_HEADER
                }</h5>`;
                PrintElem.innerHTML = header;
                PrintElem.appendChild(tableStat);
                return PrintElem;
            }
            return null;
        },
    });
    useEffect(() => {
        getSummaryData();
        if (currentDealers && dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            setBrand(getBrandByDealerNumber(dealer.brand, dealer.dealerNumber));
            setCountry(dealer.country);
            getSummaryData();
        }
    }, [dealerDataInfo]);

    const updateOpenLayer = (open: boolean): void => {
        setOpen(open);
    };

    const updateEditedValues = (editObj: any): void => {
        setIsDetailsLoading(true);
        getEditedData(vin, acNo, editObj);
    };

    const handleClickItem = (vin: string, accountNumber: number) => {
        setOpen((prev) => !prev);
        setDetailsData({});
        setVin(vin);
        setAcNo(accountNumber);
        setIsDetailsLoading(true);
        getDetailsData(vin, accountNumber).then().catch();
    };

    async function getSummaryData() {
        const service = new DealerRentalService();
        try {
            const res = await service.getSummary(
                currentDealers?.default as string,
            );
            setIsLoading(false);
            if (res && res.data) {
                setSummaryData(service.mapResponse(res.data));
            }
        } catch (error) {
            setIsLoading(false);
            console.error(error);
        }
    }

    async function getDetailsData(vin?: any, accountNumber?: any) {
        const service = new DealerRentalService();
        setIsEditApiError(false);
        try {
            setIsDetailsLoading(true);
            const res = await service.getDetails(
                currentDealers?.default as string,
                vin,
                country,
                accountNumber,
            );
            if (res && res.data) {
                setDetailsData(res.data);
                setIsEditApiError(false);
            }
        } catch (error) {
            setIsEditApiError(true);
            console.error(error);
        } finally {
            setIsDetailsLoading(false);
        }
    }

    async function getEditedData(
        vin?: any,
        accountNumber?: any,
        editObj?: any,
    ) {
        const service = new DealerRentalService();
        const { StockNumber, Mileage, MileageDate, Notes } = editObj;
        try {
            setIsDetailsLoading(true);
            const res = await service.editDetails(
                currentDealers?.default as string,
                vin,
                accountNumber,
                StockNumber,
                parseInt(Mileage),
                MileageDate,
                Notes,
            );
            if (res && res.data) {
                getDetailsData(vin, accountNumber);
                setIsEditApiError(false);
            }
        } catch (error) {
            setIsEditApiError(true);
            console.error(error);
        } finally {
            setIsDetailsLoading(false);
        }
    }
    return (
        <>
            <div className='dealer-rental-wrapper'>
                <div className='page-title'>
                    <h5>{dealerRentalInventory.LABEL_DR_HEADER}</h5>
                    {summaryData.length > NUMBER_ZERO && (
                        <button
                            className='c-btn c-btn--link c-btn--small c-btn--simple'
                            onClick={handlePrint}
                        >
                            <i
                                className='c-btn__icon c-icon c-icon--[semantic-print]'
                                aria-hidden='true'
                                role='img'
                            ></i>
                            <span className='c-btn__text'>
                                {labelText.LABEL_TEXT_PRINT}
                            </span>
                        </button>
                    )}
                </div>
                {isLoading ? (
                    <div className='main-container'>
                        <div className='loader_container'>
                            <Spinner />
                        </div>
                    </div>
                ) : summaryData.length > Number('0') ? (
                    <div className='main-container'>
                        <>
                            <Table
                                items={displayedContent}
                                onClick={handleClickItem}
                                forPrint={false}
                            />
                            <Table
                                ref={componentRef}
                                items={summaryData}
                                onClick={handleClickItem}
                                forPrint={true}
                            />
                            <TableFooter
                                brand={brand}
                                content={summaryData}
                                setDisplayedContent={setDisplayedContent}
                            />
                        </>
                    </div>
                ) : null}

                {summaryData.length === NUMBER_ZERO && !isLoading && (
                    <div className='no-content'>
                        <div>
                            No dealer rentals were found. Please try again
                            later.
                        </div>
                    </div>
                )}
                <div style={{ marginTop: 'auto', gridColumnStart: 2 }}>
                    <div style={{ marginTop: '40px' }}>
                        <FooterPrivacy />
                    </div>
                </div>
            </div>
            <DetailsView
                data={detailsData}
                detailsTitle={TITLE_DEALER_RENTAL}
                open={open}
                isDetailsLoading={isDetailsLoading}
                isEditApiError={isEditApiError}
                updateEditedValues={updateEditedValues}
                updateOpenLayer={updateOpenLayer}
            />
        </>
    );
};
export default DealerRentalPage;
